/**
 * Form for creating/editing a schedule window or marking a date off.
 *
 * Open trigger is `useEditViewState.editor` (an `EditorPrefill | null`). The dialog
 * subscribes; whenever a non-null prefill lands the dialog opens with `form.reset(prefill)`.
 *
 * Save dispatches into `useScheduleSource` mutators. Branches:
 *   workingHours = false       → upsertException(windows: null)            // off-day
 *   workingHours = true, scope = 'date'      → upsertException(windows: [{...}])
 *   workingHours = true, scope = 'recurring' → setRecurringWindow + drop overriding exception
 *
 * Schema validation is conditional via `superRefine`: time inputs are skipped when the
 * day is being marked off (inputs are disabled in the UI in that case).
 */
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  Switch,
} from '@ludus/ui'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useScheduleSource } from '../../data/useScheduleSource.ts'
import type { EditorPrefill, Weekday } from '../../types.ts'
import { fromISODate, weekdayFromDate } from '../../utils/weekRange.ts'
import { formatMinute, parseTime } from '../WeekGrid/timeMath.ts'
import { useEditViewState } from '../useEditViewState.ts'
import { editorSchema, type EditorFormValues } from './schema.ts'

const WEEKDAY_NAME_KEY: Record<Weekday, string> = {
  0: 'pages.calendar.weekday.sun',
  1: 'pages.calendar.weekday.mon',
  2: 'pages.calendar.weekday.tue',
  3: 'pages.calendar.weekday.wed',
  4: 'pages.calendar.weekday.thu',
  5: 'pages.calendar.weekday.fri',
  6: 'pages.calendar.weekday.sat',
}

export function WindowEditorDialog() {
  const editor = useEditViewState((s) => s.editor)
  const closeEditor = useEditViewState((s) => s.closeEditor)
  return (
    <Dialog open={editor !== null} onOpenChange={(open) => !open && closeEditor()}>
      {editor && <EditorBody prefill={editor} onClose={closeEditor} />}
    </Dialog>
  )
}

function EditorBody({ prefill, onClose }: { prefill: EditorPrefill; onClose: () => void }) {
  const { t } = useTranslation()
  const { setRecurringWindow, removeRecurringWindow, upsertException, removeException } =
    useScheduleSource()

  const isEditing = prefill.existing !== undefined
  const weekday = (weekdayFromDate(fromISODate(prefill.date)) ?? prefill.weekday) as Weekday
  const weekdayName = t(WEEKDAY_NAME_KEY[weekday])

  const form = useForm<EditorFormValues>({
    resolver: zodResolver(editorSchema),
    defaultValues: {
      start: formatMinute(prefill.startMinute),
      end: formatMinute(prefill.endMinute),
      workingHours: prefill.workingHours,
      scope: prefill.scope,
    },
  })

  useEffect(() => {
    form.reset({
      start: formatMinute(prefill.startMinute),
      end: formatMinute(prefill.endMinute),
      workingHours: prefill.workingHours,
      scope: prefill.scope,
    })
  }, [prefill, form])

  const workingHours = form.watch('workingHours')

  // Off-day cannot be a recurring template in this data model.
  useEffect(() => {
    if (!workingHours && form.getValues('scope') !== 'date') {
      form.setValue('scope', 'date')
    }
  }, [workingHours, form])

  const onSubmit = form.handleSubmit((values) => {
    if (!values.workingHours) {
      upsertException({
        managerId: prefill.managerId,
        date: prefill.date,
        windows: null,
      })
      onClose()
      return
    }

    const startMinute = parseTime(values.start)!
    const endMinute = parseTime(values.end)!

    if (values.scope === 'recurring') {
      const existingIndex = prefill.existing?.kind === 'template' ? prefill.existing.index : null
      setRecurringWindow({
        managerId: prefill.managerId,
        weekday,
        index: existingIndex,
        window: { startMinute, endMinute },
      })
      // If the user converts an off-day or windowed exception into a recurring template,
      // drop the exception so the template visibly applies to that date.
      if (prefill.existing?.kind === 'exception') {
        removeException({
          managerId: prefill.managerId,
          date: prefill.existing.date,
          index: prefill.existing.index,
        })
      }
    } else {
      upsertException({
        managerId: prefill.managerId,
        date: prefill.date,
        windows: [{ startMinute, endMinute }],
      })
    }
    onClose()
  })

  const onDelete = () => {
    if (!prefill.existing) return
    if (prefill.existing.kind === 'template') {
      removeRecurringWindow({
        managerId: prefill.managerId,
        weekday: prefill.existing.weekday,
        index: prefill.existing.index,
      })
    } else {
      removeException({
        managerId: prefill.managerId,
        date: prefill.existing.date,
        index: prefill.existing.index,
      })
    }
    onClose()
  }

  const titleKey = !workingHours
    ? 'pages.calendar.editor.titleOff'
    : isEditing
      ? 'pages.calendar.editor.titleEdit'
      : 'pages.calendar.editor.titleCreate'

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <div>
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription>{prefill.date}</DialogDescription>
        </div>
        <DialogClose />
      </DialogHeader>

      <form onSubmit={onSubmit}>
        <DialogBody className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <FormField
              label={t('pages.calendar.editor.start')}
              error={form.formState.errors.start?.message}
            >
              <Input
                {...form.register('start')}
                placeholder="09:00"
                inputMode="numeric"
                disabled={!workingHours}
              />
            </FormField>
            <FormField
              label={t('pages.calendar.editor.end')}
              error={form.formState.errors.end?.message}
            >
              <Input
                {...form.register('end')}
                placeholder="17:00"
                inputMode="numeric"
                disabled={!workingHours}
              />
            </FormField>
          </div>

          <FormField label={t('pages.calendar.editor.scope')}>
            <Controller
              name="scope"
              control={form.control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(v) => field.onChange(v)}
                  options={[
                    { value: 'date', label: t('pages.calendar.editor.scopeDate') },
                    {
                      value: 'recurring',
                      label: t('pages.calendar.editor.scopeRecurring', {
                        weekday: weekdayName,
                      }),
                    },
                  ]}
                  className="w-full"
                  triggerClassName="w-full justify-between"
                  disabled={!workingHours}
                />
              )}
            />
          </FormField>

          <div className="flex items-center justify-between rounded-md border border-border/60 bg-background-secondary px-3 py-2.5">
            <div className="flex flex-col">
              <span className="text-sm font-medium text-foreground">
                {t('pages.calendar.editor.workingHours')}
              </span>
              <span className="text-xs text-muted">
                {workingHours
                  ? t('pages.calendar.editor.workingHoursHint')
                  : t('pages.calendar.editor.offDayHint')}
              </span>
            </div>
            <Controller
              name="workingHours"
              control={form.control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={(v) => field.onChange(v)}
                  aria-label={t('pages.calendar.editor.workingHours')}
                />
              )}
            />
          </div>
        </DialogBody>

        <DialogFooter>
          {isEditing && (
            <Button type="button" variant="destructive" size="sm" onClick={onDelete}>
              {t('pages.calendar.editor.delete')}
            </Button>
          )}
          <div className="flex flex-1 items-center justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              {t('pages.calendar.editor.cancel')}
            </Button>
            <Button type="submit" variant="primary" size="sm">
              {t('pages.calendar.editor.save')}
            </Button>
          </div>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}

function FormField({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-xs font-medium uppercase tracking-wide text-muted">{label}</span>
      {children}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </label>
  )
}
